import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckCircle,
  Info,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react/dist/ssr";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

const alertVariants = cva(
  "relative w-full rounded-xl border p-4 flex gap-3 items-start",
  {
    variants: {
      variant: {
        info: "border-[var(--sahha-info,#3b82f6)]/20 bg-[var(--sahha-info-bg,#e8f1f9)] text-[var(--sahha-info,#3b82f6)]",
        success:
          "border-emerald-200 bg-emerald-50 text-emerald-800",
        warning:
          "border-amber-200 bg-amber-50 text-amber-900",
        danger:
          "border-red-200 bg-red-50 text-[var(--sahha-danger,#DC2626)]",
      },
    },
    defaultVariants: { variant: "info" },
  },
);

const icons = {
  info: Info,
  success: CheckCircle,
  warning: WarningCircle,
  danger: XCircle,
} as const;

type AlertProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & {
    title?: ReactNode;
    icon?: ReactNode | false;
  };

export function Alert({
  className,
  variant,
  title,
  icon,
  children,
  ...props
}: AlertProps) {
  const Icon = icons[variant ?? "info"];
  return (
    <div
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {icon === false ? null : icon ?? (
        <Icon size={20} weight="fill" className="shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        {title ? (
          <div className="font-semibold leading-snug mb-0.5">{title}</div>
        ) : null}
        <div className="text-sm leading-snug">{children}</div>
      </div>
    </div>
  );
}

export { alertVariants };
